/* eslint-disable */
import { useCallback, useEffect, useState } from 'react';
import { DatePicker } from 'antd';
import { useHistory } from 'react-router-dom';

import AbstractTabs from '../Entity/AbstractTabs';
import GenericTable from '../../components/GenericTable';
import { InvertFillButton } from '../../components/InvertFillButton';
import Dropdown from '../../components/Dropdown';
import {
  PAYMENT_VOUCHER_ACCOUNTING_STATUS_OPTIONS,
  PAYMENT_VOUCHER_METHOD_OPTIONS,
  PAYMENT_VOUCHER_STATUS_OPTIONS,
} from '../../constants/AppConstant';
import { toastError, toastSuccess } from '../../service/Toast';
import {
  accountingStatusValue,
  displayDate,
  downloadVoucherListExcel,
  downloadVoucherListPdf,
  fetchAllVouchers,
  money,
  statusTag,
  voucherStatusDate,
} from '../../components/PaymentVoucher/helpers';

const { RangePicker } = DatePicker;

const PaymentVoucherReport = () => {
  const history = useHistory();
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('All');
  const [paymentMethod, setPaymentMethod] = useState('All');
  const [accountingStatus, setAccountingStatus] = useState('All');
  const [range, setRange] = useState<any>(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      // Server-side filter on the voucher status date (settled / cancelled /
      // last payment date), paged through so nothing is cut off at 100 rows.
      const result = await fetchAllVouchers({
        status,
        paymentMethod,
        accountingStatus,
        range,
      });
      if (!result.ok) {
        setVouchers([]);
        toastError(result.message || 'Failed to fetch voucher report');
        return;
      }
      setVouchers(result.rows);
      if (result.truncated) {
        toastError(
          `Only the first ${result.rows.length} of ${result.totalCount} vouchers were loaded. Narrow the date range to see the rest.`
        );
      }
    } catch (error) {
      setVouchers([]);
      toastError('Failed to fetch voucher report');
    } finally {
      setLoading(false);
    }
  }, [status, paymentMethod, accountingStatus, range]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const downloadExcel = () => {
    try {
      downloadVoucherListExcel(vouchers);
      toastSuccess('Excel report downloaded');
    } catch (error) {
      toastError('Failed to download Excel report');
    }
  };

  const downloadPdf = () => {
    try {
      downloadVoucherListPdf(vouchers);
      toastSuccess('PDF report downloaded');
    } catch (error) {
      toastError('Failed to download PDF report');
    }
  };

  const statusOptions = [
    { id: 'all-statuses', name: 'All Status', value: 'All' },
    ...PAYMENT_VOUCHER_STATUS_OPTIONS,
  ];
  const paymentMethodOptions = [
    { id: 'all-payment-methods', name: 'All Payment Methods', value: 'All' },
    ...PAYMENT_VOUCHER_METHOD_OPTIONS,
  ];
  const accountingStatusOptions = [
    { id: 'all-accounting-statuses', name: 'All Accounting Status', value: 'All' },
    ...PAYMENT_VOUCHER_ACCOUNTING_STATUS_OPTIONS,
  ];

  return (
    <AbstractTabs
      activeKey="0"
      entityName="Payment Voucher Report"
      updateFormVisibility={() => undefined}
      onTabClick={() => undefined}
    >
      <div style={{ padding: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            padding: '0 0 20px',
          }}
        >
          <RangePicker
            value={range}
            onChange={setRange}
            format="DD-MM-YYYY"
            style={{
              height: '38px',
              borderRadius: '8px',
              background: '#f8fafc',
              border: '1px solid #dbe3ef',
              minWidth: '230px',
            }}
          />
          <Dropdown
            filterName="All Status"
            filterKey="status"
            selectedValue={status === 'All' ? undefined : status}
            checkBoxOptions={statusOptions}
            onSubmitFilter={(_, value) => setStatus(value || 'All')}
          />
          <Dropdown
            filterName="All Payment Methods"
            filterKey="paymentMethod"
            selectedValue={paymentMethod === 'All' ? undefined : paymentMethod}
            checkBoxOptions={paymentMethodOptions}
            onSubmitFilter={(_, value) => setPaymentMethod(value || 'All')}
          />
          <Dropdown
            filterName="All Accounting Status"
            filterKey="accountingStatus"
            selectedValue={accountingStatus === 'All' ? undefined : accountingStatus}
            checkBoxOptions={accountingStatusOptions}
            onSubmitFilter={(_, value) => setAccountingStatus(value || 'All')}
          />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0 }}>
            <InvertFillButton
              variant="blue"
              fontSize="base"
              fontWeight="bold"
              onClick={() => history.push('/documitra-reports')}
            >
              Download Report
            </InvertFillButton>
          </div>
        </div>
        <GenericTable
          rowKey={record => record.listKey || `${record.id}-${record.memberId || '0'}`}
          loading={loading}
          bordered
          dataSource={vouchers}
          pagination={{ pageSize: 10, showTotal: (total: number) => `Total ${total} vouchers` }}
          columns={[
            {
              title: 'Voucher ID',
              dataIndex: 'displayVoucherId',
              render: (value: any, record: any) => value || record.voucherId,
            },
            { title: 'Order ID', dataIndex: 'displayOrderId' },
            {
              title: 'Customer Name',
              dataIndex: 'customerDisplayName',
              render: (value: any, record: any) => value || record.customerName || '—',
            },
            {
              title: 'Received Amount',
              dataIndex: 'receivedAmount',
              render: (value: any, record: any) => money(value, record.currency),
            },
            {
              title: 'Govt Fee',
              dataIndex: 'memberGovtFee',
              render: (value: any, record: any) => money(value ?? 0, record.currency),
            },
            {
              title: 'Courier Label Fee',
              dataIndex: 'memberCourierFee',
              render: (value: any, record: any) => money(value ?? 0, record.currency),
            },
            {
              title: 'Settled Amount',
              dataIndex: 'settledAmount',
              render: (value: any, record: any) => money(value, record.currency),
            },
            { title: 'Status', dataIndex: 'status', render: statusTag },
            {
              title: 'Accounting Status',
              dataIndex: 'accountingStatus',
              render: (_: any, record: any) => accountingStatusValue(record),
            },
            {
              title: 'Status Date',
              dataIndex: 'statusDate',
              render: (_: any, record: any) => displayDate(voucherStatusDate(record)),
            },
            {
              title: 'Created Date',
              dataIndex: 'createdAt',
              render: (value: any) => displayDate(value, true),
            },
          ]}
        />
      </div>
    </AbstractTabs>
  );
};

export default PaymentVoucherReport;
